import React, { useState, useEffect, useCallback } from "react";
import { Pagination } from "flowbite-react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Search,
  FileText,
  Loader2,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  AlertTriangle,
  RefreshCw,
  Eye,
  X,
  Download,
  History,
  User,
  Calendar,
  Mail,
  ShieldCheck,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getToken } from "../utils/auth";

const STATUS_CONFIG = {
  RECEIVED: {
    label: "Pending",
    color: "bg-yellow-100 text-yellow-800 border border-yellow-200",
    icon: <Clock className="w-3.5 h-3.5" />,
  },
  APPROVED: {
    label: "Approved",
    color: "bg-green-100 text-green-800 border border-green-200",
    icon: <CheckCircle className="w-3.5 h-3.5" />,
  },
  REJECTED: {
    label: "Rejected",
    color: "bg-red-100 text-red-800 border border-red-200",
    icon: <XCircle className="w-3.5 h-3.5" />,
  },
};

const CLAIM_TYPE_LABELS = {
  BURSARY: "Bursary",
  LOCAL_LOAN: "Local Loan",
  ABROAD_SCHOLARSHIP: "Abroad Scholarship",
};

export default function StudentClaimsList() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("");
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, claim: null, newStatus: null });
  const [isExporting, setIsExporting] = useState(false);
  const [auditClaim, setAuditClaim] = useState(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch academic years
  const { data: academicYears = [] } = useQuery({
    queryKey: ["academicYears"],
    queryFn: async () => {
      const res = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/academic/getAcademicYears"
      );
      return res.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });

  // Set default academic year to current year
  useEffect(() => {
    if (academicYears.length > 0 && !selectedAcademicYear) {
      const currentYearCode = new Date().getFullYear().toString();
      const match = academicYears.find((y) => y.academicYearCode === currentYearCode);
      setSelectedAcademicYear(match ? match.academicYearCode : academicYears[0].academicYearCode);
    }
  }, [academicYears, selectedAcademicYear]);

  // Fetch paginated claims
  const fetchClaims = useCallback(async () => {
    const params = {
      page: currentPage - 1,
      size: pageSize,
    };
    if (selectedStatus) params.status = selectedStatus;
    if (selectedAcademicYear) params.academicYearCode = selectedAcademicYear;

    const res = await axios.get(
      process.env.REACT_APP_BASE_URL + "hec/studentClaim/getAllClaims",
      {
        headers: { Authorization: `Bearer ${getToken()}` },
        params,
      }
    );
    return res.data;
  }, [currentPage, pageSize, selectedStatus, selectedAcademicYear]);

  const {
    data: claimsPage,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["studentClaims", currentPage, selectedStatus, selectedAcademicYear],
    queryFn: fetchClaims,
    keepPreviousData: true,
    staleTime: 30 * 1000,
  });

  const allClaims = claimsPage?.content || [];
  const totalPages = claimsPage?.totalPages || 1;
  const totalElements = claimsPage?.totalElements || 0;

  // Client-side search filter on current page results
  const filteredClaims = debouncedSearch
    ? allClaims.filter(
        (c) =>
          c.studentNumber?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          c.claimType?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          c.claimDescription?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : allClaims;

  // Stat counts from current page (real counts come from backend totalElements)
  const statCounts = {
    total: totalElements,
    RECEIVED: claimsPage?.content?.filter((c) => c.claimStatus === "RECEIVED").length ?? 0,
    APPROVED: claimsPage?.content?.filter((c) => c.claimStatus === "APPROVED").length ?? 0,
    REJECTED: claimsPage?.content?.filter((c) => c.claimStatus === "REJECTED").length ?? 0,
  };

  const handleStatusUpdate = async (claim, newStatus) => {
    setConfirmModal({ open: false, claim: null, newStatus: null });
    setUpdatingId(claim.id);
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}hec/studentClaim/updateStatus/${claim.id}`,
        null,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
          params: { status: newStatus },
        }
      );
      toast.success(
        `Claim ${newStatus === "APPROVED" ? "approved" : "rejected"} successfully`,
        { position: "top-center", autoClose: 3000 }
      );
      queryClient.invalidateQueries(["studentClaims"]);
      if (isDetailOpen && selectedClaim?.id === claim.id) {
        setSelectedClaim({ ...selectedClaim, claimStatus: newStatus });
      }
    } catch (err) {
      toast.error("Failed to update claim status. Please try again.", {
        position: "top-center",
        autoClose: 4000,
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const openDetail = (claim) => {
    setSelectedClaim(claim);
    setIsDetailOpen(true);
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const downloadExcel = async () => {
    if (totalElements === 0) {
      toast.info("No claims to export.", { position: "top-center", autoClose: 3000 });
      return;
    }
    setIsExporting(true);
    try {
      const params = { page: 0, size: totalElements };
      if (selectedStatus) params.status = selectedStatus;
      if (selectedAcademicYear) params.academicYearCode = selectedAcademicYear;

      const res = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/studentClaim/getAllClaims",
        { headers: { Authorization: `Bearer ${getToken()}` }, params }
      );

      const rows = (res.data?.content || []).map((c, i) => ({
        "#": i + 1,
        "Student Number": c.studentNumber || "",
        "Claim Type": CLAIM_TYPE_LABELS[c.claimType] || c.claimType || "",
        "Claim Description": c.claimDescription || "",
        "Academic Year": c.academicYear?.academicYearName || c.academicYear?.academicYearCode || "",
        "Status": STATUS_CONFIG[c.claimStatus]?.label || c.claimStatus || "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [
        { wch: 5 }, { wch: 20 }, { wch: 22 }, { wch: 50 }, { wch: 18 }, { wch: 12 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Student Claims");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const yearLabel = selectedAcademicYear ? `_${selectedAcademicYear}` : "";
      const statusLabel = selectedStatus ? `_${selectedStatus}` : "";
      saveAs(new Blob([buf], { type: "application/octet-stream" }), `Student_Claims${yearLabel}${statusLabel}.xlsx`);
      toast.success("Excel file downloaded successfully.", { position: "top-center", autoClose: 3000 });
    } catch {
      toast.error("Failed to export claims. Please try again.", { position: "top-center", autoClose: 4000 });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <TopBarComponent />
      <div className="flex flex-1">
        <SidbarComponent />

        <main className="flex-1 p-6 lg:p-8 overflow-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-7 h-7 text-cyan-600" />
                  Student Claims
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Review and manage student scholarship rejection claims
                </p>
              </div>
              <button
                onClick={() => refetch()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-cyan-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              label="Total Claims"
              value={totalElements}
              icon={<FileText className="w-5 h-5" />}
              color="cyan"
              onClick={() => { setSelectedStatus(""); setCurrentPage(1); }}
              active={!selectedStatus}
            />
            <StatCard
              label="Pending"
              value={selectedStatus === "RECEIVED" ? totalElements : "–"}
              icon={<Clock className="w-5 h-5" />}
              color="yellow"
              onClick={() => { setSelectedStatus("RECEIVED"); setCurrentPage(1); }}
              active={selectedStatus === "RECEIVED"}
            />
            <StatCard
              label="Approved"
              value={selectedStatus === "APPROVED" ? totalElements : "–"}
              icon={<CheckCircle className="w-5 h-5" />}
              color="green"
              onClick={() => { setSelectedStatus("APPROVED"); setCurrentPage(1); }}
              active={selectedStatus === "APPROVED"}
            />
            <StatCard
              label="Rejected"
              value={selectedStatus === "REJECTED" ? totalElements : "–"}
              icon={<XCircle className="w-5 h-5" />}
              color="red"
              onClick={() => { setSelectedStatus("REJECTED"); setCurrentPage(1); }}
              active={selectedStatus === "REJECTED"}
            />
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by student number, type or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={selectedStatus}
                  onChange={handleFilterChange(setSelectedStatus)}
                  className="pl-9 pr-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none appearance-none bg-white cursor-pointer"
                >
                  <option value="">All Statuses</option>
                  <option value="RECEIVED">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Academic Year Filter */}
              <div className="relative">
                <select
                  value={selectedAcademicYear}
                  onChange={handleFilterChange(setSelectedAcademicYear)}
                  className="pl-4 pr-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none appearance-none bg-white cursor-pointer"
                >
                  <option value="">All Years</option>
                  {academicYears.map((y) => (
                    <option key={y.id} value={y.academicYearCode}>
                      {y.academicYearName || y.academicYearCode}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Export Button */}
              <button
                onClick={downloadExcel}
                disabled={isExporting || totalElements === 0}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {isExporting ? "Exporting..." : "Export Excel"}
              </button>
            </div>
          </div>

          {/* Claims Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                <Loader2 className="w-10 h-10 animate-spin text-cyan-500 mb-3" />
                <p className="text-sm">Loading claims...</p>
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
                <p className="text-sm font-medium text-red-600">Failed to load claims</p>
                <button
                  onClick={() => refetch()}
                  className="mt-3 text-sm text-cyan-600 hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : filteredClaims.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                <FileText className="w-10 h-10 mb-3" />
                <p className="text-sm font-medium">No claims found</p>
                <p className="text-xs mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Student Number</th>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Claim Type</th>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Academic Year</th>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredClaims.map((claim, index) => {
                        const statusCfg = STATUS_CONFIG[claim.claimStatus] || STATUS_CONFIG.RECEIVED;
                        const rowNum = (currentPage - 1) * pageSize + index + 1;
                        const isUpdating = updatingId === claim.id;

                        return (
                          <tr
                            key={claim.id}
                            className="hover:bg-gray-50 transition-colors group"
                          >
                            <td className="px-5 py-4 text-gray-400 text-xs font-mono">{rowNum}</td>
                            <td className="px-5 py-4">
                              <span className="font-semibold text-gray-900">{claim.studentNumber}</span>
                            </td>
                            <td className="px-5 py-4">
                              <span className="text-gray-600">
                                {CLAIM_TYPE_LABELS[claim.claimType] || claim.claimType || "—"}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <span className="text-gray-600">
                                {claim.academicYear?.academicYearName || claim.academicYear?.academicYearCode || "—"}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}
                              >
                                {statusCfg.icon}
                                {statusCfg.label}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-2">
                                {/* View Detail */}
                                <button
                                  onClick={() => openDetail(claim)}
                                  className="p-1.5 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                                  title="View details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>

                                {/* Audit / Who processed */}
                                <button
                                  onClick={() => setAuditClaim(claim)}
                                  className="p-1.5 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                                  title="View audit info"
                                >
                                  <History className="w-4 h-4" />
                                </button>

                                {/* Approve / Reject — only for RECEIVED claims */}
                                {claim.claimStatus === "RECEIVED" && (
                                  <>
                                    <button
                                      onClick={() =>
                                        setConfirmModal({ open: true, claim, newStatus: "APPROVED" })
                                      }
                                      disabled={isUpdating}
                                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
                                    >
                                      {isUpdating ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <CheckCircle className="w-3.5 h-3.5" />
                                      )}
                                      Approve
                                    </button>
                                    <button
                                      onClick={() =>
                                        setConfirmModal({ open: true, claim, newStatus: "REJECTED" })
                                      }
                                      disabled={isUpdating}
                                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                                    >
                                      {isUpdating ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <XCircle className="w-3.5 h-3.5" />
                                      )}
                                      Reject
                                    </button>
                                  </>
                                )}

                                {/* Revert to RECEIVED for already-processed claims */}
                                {(claim.claimStatus === "APPROVED" || claim.claimStatus === "REJECTED") && (
                                  <button
                                    onClick={() =>
                                      setConfirmModal({ open: true, claim, newStatus: "RECEIVED" })
                                    }
                                    disabled={isUpdating}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors"
                                  >
                                    <RefreshCw className="w-3.5 h-3.5" />
                                    Revert
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Footer: count + pagination */}
                <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-4 border-t border-gray-100 gap-3">
                  <p className="text-sm text-gray-500">
                    Showing{" "}
                    <span className="font-medium text-gray-700">
                      {(currentPage - 1) * pageSize + 1}–
                      {Math.min(currentPage * pageSize, totalElements)}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium text-gray-700">{totalElements}</span>{" "}
                    claims
                  </p>
                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(p) => setCurrentPage(p)}
                      showIcons
                      theme={{
                        pages: {
                          selector: {
                            active: "bg-cyan-600 text-white border-cyan-600 hover:bg-cyan-700",
                          },
                        },
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      <FooterComponents />

      {/* Detail Modal */}
      {isDetailOpen && selectedClaim && (
        <DetailModal
          claim={selectedClaim}
          onClose={() => setIsDetailOpen(false)}
          onApprove={() => setConfirmModal({ open: true, claim: selectedClaim, newStatus: "APPROVED" })}
          onReject={() => setConfirmModal({ open: true, claim: selectedClaim, newStatus: "REJECTED" })}
          updatingId={updatingId}
        />
      )}

      {/* Confirm Modal */}
      {confirmModal.open && (
        <ConfirmModal
          claim={confirmModal.claim}
          newStatus={confirmModal.newStatus}
          onConfirm={() => handleStatusUpdate(confirmModal.claim, confirmModal.newStatus)}
          onCancel={() => setConfirmModal({ open: false, claim: null, newStatus: null })}
        />
      )}

      {/* Audit Modal */}
      {auditClaim && (
        <AuditModal
          claim={auditClaim}
          onClose={() => setAuditClaim(null)}
        />
      )}
    </div>
  );
}

// ── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, color, onClick, active }) {
  const colorMap = {
    cyan: {
      bg: active ? "bg-cyan-600" : "bg-white",
      text: active ? "text-white" : "text-cyan-600",
      label: active ? "text-cyan-100" : "text-gray-500",
      value: active ? "text-white" : "text-gray-900",
      border: active ? "border-cyan-600" : "border-gray-200",
    },
    yellow: {
      bg: active ? "bg-yellow-500" : "bg-white",
      text: active ? "text-white" : "text-yellow-500",
      label: active ? "text-yellow-100" : "text-gray-500",
      value: active ? "text-white" : "text-gray-900",
      border: active ? "border-yellow-500" : "border-gray-200",
    },
    green: {
      bg: active ? "bg-green-600" : "bg-white",
      text: active ? "text-white" : "text-green-600",
      label: active ? "text-green-100" : "text-gray-500",
      value: active ? "text-white" : "text-gray-900",
      border: active ? "border-green-600" : "border-gray-200",
    },
    red: {
      bg: active ? "bg-red-600" : "bg-white",
      text: active ? "text-white" : "text-red-600",
      label: active ? "text-red-100" : "text-gray-500",
      value: active ? "text-white" : "text-gray-900",
      border: active ? "border-red-600" : "border-gray-200",
    },
  };
  const c = colorMap[color];

  return (
    <button
      onClick={onClick}
      className={`${c.bg} ${c.border} rounded-xl border shadow-sm p-5 text-left w-full transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer`}
    >
      <div className={`${c.text} mb-3`}>{icon}</div>
      <p className={`text-2xl font-bold ${c.value}`}>{value}</p>
      <p className={`text-xs font-medium mt-1 ${c.label}`}>{label}</p>
    </button>
  );
}

// ── Detail Modal ─────────────────────────────────────────────────────────────
function DetailModal({ claim, onClose, onApprove, onReject, updatingId }) {
  const statusCfg = STATUS_CONFIG[claim.claimStatus] || STATUS_CONFIG.RECEIVED;
  const isUpdating = updatingId === claim.id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-cyan-50 to-white">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Claim Details</h2>
            <p className="text-xs text-gray-500 mt-0.5">Student: {claim.studentNumber}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Status badge */}
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusCfg.color}`}>
              {statusCfg.icon}
              {statusCfg.label}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <DetailField label="Student Number" value={claim.studentNumber} />
            <DetailField
              label="Claim Type"
              value={CLAIM_TYPE_LABELS[claim.claimType] || claim.claimType || "—"}
            />
            <DetailField
              label="Academic Year"
              value={claim.academicYear?.academicYearName || claim.academicYear?.academicYearCode || "—"}
            />
            <DetailField label="Status" value={statusCfg.label} />
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Claim Description
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 leading-relaxed min-h-[80px]">
              {claim.claimDescription || <span className="text-gray-400 italic">No description provided</span>}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        {claim.claimStatus === "RECEIVED" && (
          <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
            <button
              onClick={onReject}
              disabled={isUpdating}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              <XCircle className="w-4 h-4" />
              Reject Claim
            </button>
            <button
              onClick={onApprove}
              disabled={isUpdating}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {isUpdating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Approve Claim
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm font-medium text-gray-800">{value || "—"}</p>
    </div>
  );
}

// ── Audit Modal ──────────────────────────────────────────────────────────────
function AuditModal({ claim, onClose }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios
      .get(process.env.REACT_APP_BASE_URL + `hec/studentClaim/audit/${claim.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => { if (!cancelled) { setAudit(res.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [claim.id]);

  const fmt = (dt) => {
    if (!dt) return "—";
    return new Date(dt).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const statusCfg = STATUS_CONFIG[claim.claimStatus] || STATUS_CONFIG.RECEIVED;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-violet-50 to-white">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-violet-600" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Audit Information</h2>
              <p className="text-xs text-gray-500 mt-0.5">Student: {claim.studentNumber}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin text-violet-500 mb-2" />
              <p className="text-sm">Loading audit info...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <AlertTriangle className="w-8 h-8 text-red-400 mb-2" />
              <p className="text-sm text-red-600">Failed to load audit information</p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Current status */}
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusCfg.color}`}>
                  {statusCfg.icon}
                  {statusCfg.label}
                </span>
              </div>

              {/* Timeline */}
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="mt-0.5 p-1.5 bg-cyan-100 rounded-md">
                    <Calendar className="w-4 h-4 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Submitted On</p>
                    <p className="text-sm font-medium text-gray-800 mt-0.5">{fmt(audit.submittedAt)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="mt-0.5 p-1.5 bg-violet-100 rounded-md">
                    <Clock className="w-4 h-4 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Last Updated</p>
                    <p className="text-sm font-medium text-gray-800 mt-0.5">{fmt(audit.lastUpdatedAt)}</p>
                  </div>
                </div>
              </div>

              {/* Processed by */}
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Processed By
                </p>
                {audit.processedByName ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-violet-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{audit.processedByName}</p>
                        {audit.processedByRole && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <ShieldCheck className="w-3 h-3 text-gray-400" />
                            <p className="text-xs text-gray-500">{audit.processedByRole}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    {audit.processedByEmail && (
                      <div className="flex items-center gap-2 ml-12">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                        <p className="text-xs text-gray-500">{audit.processedByEmail}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Clock className="w-4 h-4" />
                    <p className="text-sm italic">Not yet processed</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Confirm Modal ─────────────────────────────────────────────────────────────
function ConfirmModal({ claim, newStatus, onConfirm, onCancel }) {
  const isApproving = newStatus === "APPROVED";
  const isReverting = newStatus === "RECEIVED";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-60">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
            isApproving
              ? "bg-green-100"
              : isReverting
              ? "bg-gray-100"
              : "bg-red-100"
          }`}
        >
          {isApproving ? (
            <CheckCircle className="w-6 h-6 text-green-600" />
          ) : isReverting ? (
            <RefreshCw className="w-6 h-6 text-gray-600" />
          ) : (
            <AlertTriangle className="w-6 h-6 text-red-600" />
          )}
        </div>

        <h3 className="text-lg font-bold text-gray-900 text-center">
          {isApproving ? "Approve Claim?" : isReverting ? "Revert Claim?" : "Reject Claim?"}
        </h3>
        <p className="text-sm text-gray-500 text-center mt-2 mb-6">
          {isApproving
            ? `You are about to approve the claim for student ${claim.studentNumber}. This action can be reverted.`
            : isReverting
            ? `This will mark the claim for student ${claim.studentNumber} back to Pending.`
            : `You are about to reject the claim for student ${claim.studentNumber}. This action can be reverted.`}
        </p>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${
              isApproving
                ? "bg-green-600 hover:bg-green-700"
                : isReverting
                ? "bg-gray-600 hover:bg-gray-700"
                : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {isApproving ? "Yes, Approve" : isReverting ? "Yes, Revert" : "Yes, Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}
